import React, {useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation} from 'react-router-dom'
import {setLinkType} from '../../app-store/kpGlobalStateSlice'
import {
  resetCatalogCardCollapseStatus,
  resetParentCardCollapseStatus,
  resetToDefaultCart,
} from '../../app-store/kp11CartSlice'
import {resetCouponData} from '../../app-store/kp11CouponSlice'
import {getUserRoles} from '../../utils/get-user-roles'
import {
  pendingActionsIcon,
  taskAltIcon,
  sidebarPurchaseLinksIcon,
} from '../../assets'
import {NAV_ICONS} from './nav-icons'
import {SideNav} from './side-nav'

const CONTROL_CENTER_PATH = '/control-center'

// Panel labels must match the option labels in
// control-center-my-accounts/control-center.js (selected via
// location.state.activePanel).
const PARTNER_PANELS = [
  {label: 'Scheduled Appointments', ...NAV_ICONS.appointments},
  {label: 'Active 1:Many Links', icon: sidebarPurchaseLinksIcon},
  {label: 'Pending 1:1 Links', icon: pendingActionsIcon},
  {label: 'Completed Orders', icon: taskAltIcon},
  {label: 'Account Management', ...NAV_ICONS.accounts},
]

const INFLUENCER_PANELS = [
  {label: '1:Many Purchase', icon: sidebarPurchaseLinksIcon},
  {label: 'Pending 1:1 Links', icon: pendingActionsIcon},
  {label: 'Completed Orders', icon: taskAltIcon},
  {label: 'Account Management', ...NAV_ICONS.accounts},
]

/**
 * Keap Partner flavour of the Figma side nav: same shell/design as SideNav.
 * The Control Center views live here as sub-items of "My Client Center"
 * instead of in a second in-page sidebar.
 */
function KeapPartnerSideNav({userData, ...navProps}) {
  const dispatch = useDispatch()
  const {pathname} = useLocation()
  const keapPartnerId = useSelector(state => state.keapPartner?.keapPartnerId)
  const isInfluencerOps = getUserRoles(userData).includes('InfluencerOps')

  const items = useMemo(() => {
    const panels = isInfluencerOps ? INFLUENCER_PANELS : PARTNER_PANELS
    const defaultPanel = panels[0].label
    const onSelect = () => {
      dispatch(setLinkType('1:1'))
      dispatch(resetToDefaultCart())
      dispatch(resetCouponData())
      dispatch(resetParentCardCollapseStatus())
      dispatch(resetCatalogCardCollapseStatus())
    }
    const children = panels.map(({label, icon, activeIcon}) => ({
      id: `cc-${label}`,
      label,
      icon,
      activeIcon,
      to: CONTROL_CENTER_PATH,
      panel: label,
      defaultPanel,
      state: {
        linkType: '1:1',
        userData,
        keapPartnerId,
        activePanel: label,
      },
      onSelect,
    }))
    // Section title followed by the views as always-visible items (no toggle).
    const nav = [
      {
        id: 'my-client-center',
        heading: true,
        label: isInfluencerOps ? 'Control Center' : 'My Client Center',
      },
      ...children,
    ]
    if (isInfluencerOps) {
      nav.push({
        id: 'collateral',
        label: 'Collateral & Education',
        ...NAV_ICONS.collateral,
        to: CONTROL_CENTER_PATH,
        panel: 'Collateral & Education',
        defaultPanel,
        state: {
          linkType: '1:1',
          userData,
          keapPartnerId,
          activePanel: 'Collateral & Education',
        },
        onSelect,
      })
    }
    return nav
  }, [dispatch, userData, keapPartnerId, isInfluencerOps])

  // No side nav on landing; it appears once the user opens My Client Center
  // from the header (or any other page).
  if (pathname === '/') return null

  return <SideNav {...navProps} items={items} showFooterActions={false} />
}

export {KeapPartnerSideNav}
